class CollectionSection extends HTMLElement {
  constructor() {
    super();

    this.pageOverlayClass = "page-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.drawer = () => document.querySelector(".wt-filter");
    this.classDrawerActive = "wt-filter--drawer-open";
    this.getCloseButton = () => document.querySelector(".wt-filter__close");
    this.getTrigger = () =>
      document.querySelector(".collection__filter-trigger");
    this.isOpen = () =>
      document.body.classList.contains(this.activeOverlayBodyClass);
    this.sectionsTriggers = () =>
      this.drawer()?.querySelectorAll(".wt-collapse__trigger");
    this.isDrawer = this.dataset.filterPosition === "drawer";

    this.triggerClasses = [
      "wt-filter__close",
      this.pageOverlayClass,
      "collection__filter-trigger",
    ];
    this.toggleDrawerElements = () =>
      this.drawer().querySelectorAll(this.drawer().dataset.toggleTabindex);

    this.overlay = document.createElement("div");

    this.breakpoint = 768;
    this.currentDrawerMode = this.isDrawerMode();

    // Touch swipe variables
    this.touchStartY = 0;
    this.touchCurrentY = 0;
    this.isDragging = false;
    this.swipeThreshold = 50; // Minimum distance to trigger close

    this.init();
  }

  init() {
    this.createOverlay();
    this.updateTabindexes(this.isOpen());
    this.initSwipeToClose();

    document.body.addEventListener("click", (e) => {
      if (this.triggerClasses.some((cls) => e.target.classList.contains(cls))) {
        this.toggleDrawer(e);
      }
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen()) {
          this.toggleDrawer(e);
        }
      }

      if (isTabPressed && this.isOpen() && this.currentDrawerMode) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    window.addEventListener("resize", this.handleResize.bind(this));
    this.handleResize();
  }

  isDrawerMode() {
    const width = window.innerWidth;
    // Force drawer mode if filter trigger is in collection header
    const hasFilterTrigger = document.querySelector('.collection-title__toolbar .collection__filter-trigger');
    return this.isDrawer || width <= this.breakpoint || hasFilterTrigger;
  }

  handleResize() {
    const isDrawerMode = this.isDrawerMode();

    if (isDrawerMode !== this.currentDrawerMode) {
      this.currentDrawerMode = isDrawerMode;

      if (isDrawerMode) {
        if (!this.isOpen()) {
          this.drawer()?.classList.remove(this.classDrawerActive);
          document.body.classList.remove(this.activeOverlayBodyClass);
        }
        this.updateTabindexes(this.isOpen());
      } else {
        this.drawer()?.classList.add(this.classDrawerActive);
        document.body.classList.remove(this.activeOverlayBodyClass);
        this.updateTabindexes(true);
      }
    }
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const elements = Array.from(
      this.drawer().querySelectorAll(focusableElementsSelector),
    ).filter(
      (el) =>
        !el.hasAttribute("disabled") &&
        el.tabIndex >= 0 &&
        el.offsetParent !== null,
    );

    return {
      focusableElements: elements,
      first: elements[0],
      last: elements[elements.length - 1],
    };
  }

  updateTabindexes(isOpen) {
    if (this.currentDrawerMode) {
      if (isOpen) {
        this.getCloseButton()?.focus();
        this.temporaryHideFocusVisible();
        setTabindex(this.sectionsTriggers(), "0");
        setTabindex(this.toggleDrawerElements(), "0");
      } else {
        this.getTrigger()?.focus();
        this.temporaryHideFocusVisible();
        setTabindex(this.sectionsTriggers(), "-1");
        setTabindex(this.toggleDrawerElements(), "-1");

        this.closeAllCollapsibleSections();
      }
    } else {
      // Always visible mode
      setTabindex(this.sectionsTriggers(), "0");
      setTabindex(this.toggleDrawerElements(), "0");
    }
  }

  toggleDrawer(e) {
    if (e) e.preventDefault();

    if (!this.currentDrawerMode) {
      return;
    }

    if (this.isOpen()) {
      // close drawer
      const offsetTop = -parseInt(document.body.style.top, 10);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      window.scrollTo(0, offsetTop);

      this.closeAllCollapsibleSections();
    } else {
      // open drawer
      this.openAllCollapsibleSections();
      document.body.style.top = `${-document.documentElement.scrollTop}px`;
      document.body.style.left = "0px";
    }

    this.drawer()?.classList.toggle(this.classDrawerActive);
    document.body.classList.toggle(this.activeOverlayBodyClass);

    this.updateTabindexes(this.isOpen());
  }

  closeAllCollapsibleSections() {
    const openSections = this.drawer().querySelectorAll('[data-open="true"]');
    openSections.forEach((section) => {
      const trigger = section.querySelector(".wt-collapse__trigger");
      if (trigger) {
        trigger.classList.remove("wt-collapse__trigger--active");
        section.dataset.open = "false";
        const focusableElementsWithTabindex =
          section.querySelectorAll('[tabindex="0"]');
        setTabindex(focusableElementsWithTabindex, "-1");
      }
    });
  }

  openAllCollapsibleSections() {
    const openSections = this.drawer().querySelectorAll('[data-open="false"]');
    openSections.forEach((section) => {
      const trigger = section.querySelector(".wt-collapse__trigger");
      if (trigger) {
        trigger.classList.add("wt-collapse__trigger--active");
        section.dataset.open = "true";
        const focusableElementsWithTabindex =
          section.querySelectorAll('[tabindex="0"]');
        setTabindex(focusableElementsWithTabindex, "-1");
      }
    });
  }


  createOverlay() {
    if (!document.querySelector(`.${this.pageOverlayClass}`)) {
      this.overlay?.classList.add(this.pageOverlayClass);
      document.body.appendChild(this.overlay);
    }
  }

  initSwipeToClose() {
    const drawer = this.drawer();
    if (!drawer) return;

    const header = drawer.querySelector('.wt-filter__header');
    const body = drawer.querySelector('.wt-filter__body');
    if (!header) return;

    let startScrollTop = 0;

    // Touch start
    const handleTouchStart = (e) => {
      if (window.innerWidth >= this.breakpoint) return; // Only on mobile
      if (!this.isOpen()) return;

      this.touchStartY = e.touches[0].clientY;
      startScrollTop = body ? body.scrollTop : 0;
      this.isDragging = false; // Will be set to true in touchmove if conditions are met
      drawer.style.transition = 'none'; // Disable transition during drag
    };

    // Touch move
    const handleTouchMove = (e) => {
      if (window.innerWidth >= this.breakpoint) return;
      if (!this.isOpen()) return;

      this.touchCurrentY = e.touches[0].clientY;
      const deltaY = this.touchCurrentY - this.touchStartY;
      const currentScrollTop = body ? body.scrollTop : 0;

      // Only start dragging if:
      // 1. Dragging down (deltaY > 0)
      // 2. Body is scrolled to the top (scrollTop === 0)
      if (deltaY > 0 && currentScrollTop === 0) {
        this.isDragging = true;
        e.preventDefault(); // Prevent scroll
        
        const newBottom = -deltaY;
        drawer.style.bottom = `${newBottom}px`;
      }
    };

    // Touch end
    const handleTouchEnd = (e) => {
      if (!this.isDragging) {
        drawer.style.transition = '';
        return;
      }
      if (window.innerWidth >= this.breakpoint) return;

      this.isDragging = false;
      const deltaY = this.touchCurrentY - this.touchStartY;

      // Re-enable transition
      drawer.style.transition = '';

      // If dragged down more than threshold, close the drawer
      if (deltaY > this.swipeThreshold) {
        // Reset bottom position before closing
        drawer.style.bottom = '';
        // Small delay to ensure style is reset before toggle
        requestAnimationFrame(() => {
          this.toggleDrawer(e);
        });
      } else {
        // Snap back to open position
        drawer.style.bottom = '0';
      }

      this.touchStartY = 0;
      this.touchCurrentY = 0;
    };

    // Touch cancel
    const handleTouchCancel = () => {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      drawer.style.transition = '';
      drawer.style.bottom = '0';
      this.touchStartY = 0;
      this.touchCurrentY = 0;
    };

    // Add listeners to header
    header.addEventListener('touchstart', handleTouchStart, { passive: true });
    header.addEventListener('touchmove', handleTouchMove, { passive: false }); // passive: false to allow preventDefault
    header.addEventListener('touchend', handleTouchEnd, { passive: true });
    header.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    // Also add to body for when scrolled to top
    if (body) {
      body.addEventListener('touchstart', handleTouchStart, { passive: true });
      body.addEventListener('touchmove', handleTouchMove, { passive: false });
      body.addEventListener('touchend', handleTouchEnd, { passive: true });
      body.addEventListener('touchcancel', handleTouchCancel, { passive: true });
    }
  }
}

customElements.define("collection-section", CollectionSection);

// =============================================================================
// Grid Initial Load Manager
// Oculta el grid hasta que todas las imágenes estén cargadas
// =============================================================================
(function() {
  let gridInitialized = false;
  let hasCompletedFirstLoad = false;

  // Función para mostrar las secciones después del grid
  function showAfterSections() {
    // Simplemente quitar la clase 'grid-loading' del body
    document.body.classList.remove('grid-loading');
    
    // Reinicializar readmore para elementos de metafields después de un pequeño delay
    // para asegurar que los estilos de visibilidad se hayan aplicado
    setTimeout(() => {
      // Disparar un evento resize para forzar recalculo de readmore
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  function initGridVisibility() {
    if (gridInitialized) return;
    
    const grid = document.querySelector('#product-grid');
    const loader = document.querySelector('.infinite-scroll-loading');
    
    if (!grid) return;

    // Marcar como inicializado para evitar múltiples ejecuciones
    gridInitialized = true;

    // Primera carga: no ocultar productos iniciales.
    // Dejamos el comportamiento actual de espera para paginaciones siguientes.
    if (!hasCompletedFirstLoad) {
      hasCompletedFirstLoad = true;
      grid.classList.add('grid-loaded');
      if (loader) {
        loader.classList.remove('initial-load');
      }
      showAfterSections();
      if (window.location.hash) {
        requestAnimationFrame(() => {
          const target = document.getElementById(window.location.hash.slice(1));
          if (target) {
            const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
            window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - headerHeight, behavior: 'smooth' });
          }
        });
      }
      return;
    }

    // Añadir clase 'grid-loading' al body para ocultar secciones
    document.body.classList.add('grid-loading');

    // Añadir clase al loader para la carga inicial (si no la tiene ya)
    if (loader && !loader.classList.contains('initial-load')) {
      loader.classList.add('initial-load');
    }

    // Obtener todas las imágenes del grid
    const images = grid.querySelectorAll('img');
    const imageArray = Array.from(images);
    
    // Si no hay imágenes, mostrar el grid inmediatamente
    if (imageArray.length === 0) {
      grid.classList.add('grid-loaded');
      if (loader) {
        loader.classList.remove('initial-load');
      }
      showAfterSections();
      return;
    }

    let loadedImages = 0;
    const totalImages = imageArray.length;

    function checkAllImagesLoaded() {
      loadedImages++;
      
      if (loadedImages >= totalImages) {
        // Todas las imágenes cargadas, mostrar el grid
        requestAnimationFrame(() => {
          grid.classList.add('grid-loaded');
          if (loader) {
            loader.classList.remove('initial-load');
          }
          
          // Mostrar las secciones que vienen después del grid
          showAfterSections();
        });
      }
    }

    // Verificar cada imagen
    imageArray.forEach((img) => {
      if (img.complete && img.naturalHeight !== 0) {
        // Imagen ya cargada
        checkAllImagesLoaded();
      } else {
        // Esperar a que la imagen se cargue
        img.addEventListener('load', checkAllImagesLoaded, { once: true });
        img.addEventListener('error', checkAllImagesLoaded, { once: true }); // Contar también errores
      }
    });

    // Timeout de seguridad: mostrar el grid después de 3 segundos aunque no todas las imágenes estén cargadas
    setTimeout(() => {
      if (!grid.classList.contains('grid-loaded')) {
        grid.classList.add('grid-loaded');
        if (loader) {
          loader.classList.remove('initial-load');
        }
        showAfterSections();
      }
    }, 3000);
  }

  // Inicializar al cargar el DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGridVisibility);
  } else {
    initGridVisibility();
  }

  // Reinicializar después de cambios en filtros
  document.addEventListener('facets:change', function() {
    const grid = document.querySelector('#product-grid');
    const loader = document.querySelector('.infinite-scroll-loading');
    
    // Añadir clase 'grid-loading' al body para ocultar secciones
    document.body.classList.add('grid-loading');
    
    if (grid) {
      grid.classList.remove('grid-loaded');
    }
    if (loader) {
      loader.classList.add('initial-load');
    }
    
    gridInitialized = false;
    setTimeout(initGridVisibility, 100);
  });
})();

// =============================================================================
// Scroll to top button functionality
// =============================================================================
(function() {
  const scrollToTopBtn = document.querySelector('.scroll-to-top');
  let lastScrollTop = 0;
  let isScrollingUp = false;

  if (!scrollToTopBtn) return;

  // Detectar scroll hacia arriba
  window.addEventListener('scroll', function() {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    
    // Determinar si está haciendo scroll hacia arriba
    if (currentScroll < lastScrollTop && currentScroll > 200) {
      isScrollingUp = true;
      scrollToTopBtn.classList.add('visible');
    } else {
      isScrollingUp = false;
      scrollToTopBtn.classList.remove('visible');
    }
    
    lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
  }, { passive: true });

  // Funcionalidad del botón: scroll continuo (revertido a propósito para añadir
  // carga adicional y seguir estresando content-visibility, ya aplicado a todas
  // las colecciones, ahora que el crash no se reproduce).
  scrollToTopBtn.addEventListener('click', function() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();
